import { isGuestClaimedLayout } from "./guestLayoutStorage";
import { normalizeEmail } from "./sessionAudience";

export function readGuestSessionId() {
  try {
    return localStorage.getItem("mf_gsid") || null;
  } catch {
    return null;
  }
}

export function appendGuestSessionQuery(url) {
  const gsid = readGuestSessionId();
  if (!gsid) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}guestSessionId=${encodeURIComponent(gsid)}`;
}

export function layoutUserId(layout) {
  if (!layout?.user) return null;
  if (typeof layout.user === "object") return layout.user._id || null;
  return layout.user;
}

/** Mongo id of the layout this copy was forked from, if any. */
export function layoutCopiedFromRef(layout) {
  return layout?.baseCopiedFrom || layout?.immediateCopiedFrom || layout?.copiedFrom || null;
}

export function layoutCopiedFromId(layout) {
  const ref = layoutCopiedFromRef(layout);
  if (!ref) return null;
  if (typeof ref === "object") return ref._id ? String(ref._id) : null;
  return String(ref);
}

/**
 * Client copy / fork — viewer owns the SavedLayout but is not the listing uploader
 * for buyer inquiries (those go to the parent/original upload).
 */
export function isClientCopyLayout(layout) {
  const copiedFromId = layoutCopiedFromId(layout);
  if (!copiedFromId || !layout?._id) return false;
  return copiedFromId !== String(layout._id);
}

export function isAuthenticatedLayoutOwner({ layout, userId }) {
  const ownerId = layoutUserId(layout);
  if (!ownerId || !userId) return false;
  return String(userId) === String(ownerId);
}

export function isGuestLayoutOwner(layout, isGuest) {
  if (!isGuest || !layout) return false;
  // Only this browser's guest upload/claim counts — never layout metadata such as
  // isGuestCreatorSession, which describes how the listing was created, not who is viewing.
  return isGuestClaimedLayout(layout);
}

export function canConfigureGalleryInquiries({ layout, isGuest, userId }) {
  if (isClientCopyLayout(layout)) return false;
  return (
    isGuestLayoutOwner(layout, isGuest) ||
    isAuthenticatedLayoutOwner({ layout, userId })
  );
}

/**
 * Auth signup prefill for guests. Layout inquiryContactEmail is only used when
 * the current viewer may configure inquiries (uploader); viewers never inherit
 * the public seller contact email.
 */
export function resolveGuestAuthPrefillEmail({
  layout,
  cachedEmail = "",
  isGuest,
  userId,
}) {
  const fromCache = normalizeEmail(cachedEmail) || "";
  if (!canConfigureGalleryInquiries({ layout, isGuest, userId })) {
    return fromCache;
  }
  return normalizeEmail(layout?.inquiryContactEmail) || fromCache || "";
}

export function inquiriesAvailableForLayout(layout) {
  if (!layout) return false;
  if (layout.inquiriesAvailable != null) {
    return Boolean(layout.inquiriesAvailable);
  }
  if (layout.inquiryContactEmail) return true;

  const parentRef = layoutCopiedFromRef(layout);
  if (parentRef && typeof parentRef === "object") {
    if (parentRef.inquiriesAvailable != null) {
      return Boolean(parentRef.inquiriesAvailable);
    }
    if (parentRef.inquiryContactEmail) return true;
  }

  if (isClientCopyLayout(layout)) return false;
  return Boolean(layoutUserId(layout));
}

export function isLayoutUploaderForInquiries({ layout, isGuest, userId, isGuestOwner }) {
  if (isGuestOwner) return true;
  // Client-copy owners edit their fork but are not the listing uploader for inquiries.
  if (isClientCopyLayout(layout)) return false;
  const ownerId = layoutUserId(layout);
  return Boolean(userId && ownerId && String(userId) === String(ownerId));
}

export function shouldShowViewerInquire({ layout, isGuest, userId }) {
  if (!layout?._id && !layout?.uuid) return false;
  const guestOwner = isGuestLayoutOwner(layout, isGuest);
  if (isLayoutUploaderForInquiries({ layout, isGuest, userId, isGuestOwner: guestOwner })) {
    return false;
  }
  return inquiriesAvailableForLayout(layout);
}

const CONTACT_BANNER_DISMISS_PREFIX = "mf.guestContactBanner.dismiss.";

export function isContactBannerDismissed(layoutId) {
  if (!layoutId) return false;
  try {
    return sessionStorage.getItem(`${CONTACT_BANNER_DISMISS_PREFIX}${layoutId}`) === "1";
  } catch {
    return false;
  }
}

export function dismissContactBanner(layoutId) {
  if (!layoutId) return;
  try {
    sessionStorage.setItem(`${CONTACT_BANNER_DISMISS_PREFIX}${layoutId}`, "1");
  } catch {
    /* ignore */
  }
}

export function shouldShowGuestContactBanner({ layout, isGuest }) {
  if (!layout?._id) return false;
  if (!isGuestLayoutOwner(layout, isGuest)) return false;
  if (layout.inquiryContactEmail) return false;
  if (isContactBannerDismissed(layout._id)) return false;
  return true;
}

/**
 * Display gate for the uploader leads-email modal.
 * Schedule may arm while auth/fork is up; only hide the modal until those clear
 * so Save/signup within the first few seconds does not permanently skip it.
 */
export function shouldDisplayUploaderContactModal({
  scheduledOpen,
  authModalOpen,
  forkTransitioning = false,
}) {
  return Boolean(scheduledOpen) && !authModalOpen && !forkTransitioning;
}

export const BUYER_CONTACT_MENU_LABEL = "Capture leads via email";

const INQUIRY_SESSION_PREFIX = "mf.galleryInquiry.session.";

/**
 * Stable browser-session key for gallery inquiries.
 * Client copies (e.g. after "Shuffle this layout") get a new _id but inquiries
 * belong to the root listing — resolve to that id so gallery ↔ editor agree.
 */
export function layoutInquirySessionKey(layoutOrKey) {
  if (!layoutOrKey) return null;
  if (typeof layoutOrKey === "string") return layoutOrKey;
  const copiedFromId = layoutCopiedFromId(layoutOrKey);
  const ownId = layoutOrKey._id ? String(layoutOrKey._id) : null;
  if (copiedFromId && ownId && copiedFromId !== ownId) {
    return copiedFromId;
  }
  return layoutOrKey._id || layoutOrKey.uuid || null;
}

export function readGalleryInquirySession(layoutKey) {
  const key = layoutInquirySessionKey(layoutKey);
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(`${INQUIRY_SESSION_PREFIX}${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const email = normalizeEmail(parsed?.email);
    if (!email) return null;
    return { email, submitted: Boolean(parsed?.submitted) };
  } catch {
    return null;
  }
}

export function writeGalleryInquirySession(layoutKey, { email, submitted }) {
  const key = layoutInquirySessionKey(layoutKey);
  const normalized = normalizeEmail(email);
  if (!key || !normalized) return;
  try {
    sessionStorage.setItem(
      `${INQUIRY_SESSION_PREFIX}${key}`,
      JSON.stringify({ email: normalized, submitted: Boolean(submitted) })
    );
  } catch {
    /* ignore */
  }
}

const INTEREST_CONSENT_KEY = "mf.galleryInquiry.interestConsent";

/**
 * The address a viewer has already agreed to share with property teams, which is
 * what lets a later tap send without a confirmation step. Stored against the
 * address itself so switching accounts on a shared device asks again instead of
 * inheriting the previous viewer's answer.
 *
 * localStorage rather than session, since the whole point is to outlive the
 * visit — but a new browser or a cleared store simply means the confirmation is
 * shown once more, which is the safe direction to fail in.
 */
export function readGalleryInterestConsentEmail() {
  try {
    return normalizeEmail(localStorage.getItem(INTEREST_CONSENT_KEY)) || "";
  } catch {
    return "";
  }
}

export function recordGalleryInterestConsent(email) {
  const normalized = normalizeEmail(email);
  if (!normalized) return;
  try {
    localStorage.setItem(INTEREST_CONSENT_KEY, normalized);
  } catch {
    /* ignore */
  }
}

/** Restores the success state when this browser already inquired on the layout. */
export function galleryInquiryConfirmStateFromSession(layoutKey) {
  const session = readGalleryInquirySession(layoutKey);
  if (!session?.submitted) return null;
  return { email: session.email, kind: "submitted" };
}

/**
 * Editor bottom-bar Inquire control. Stays mounted after a submit — it swaps in place
 * to the inert "Interest sent" confirmation, matching the gallery CTA, so the bottom
 * row keeps its width instead of reflowing around a removed action.
 */
export function shouldShowEditorInquireFab({ showViewerInquire, inquiryAlreadySent }) {
  return Boolean(showViewerInquire) || Boolean(inquiryAlreadySent);
}
